import { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MdExpandMore, MdExpandLess, MdAdd, MdRemoveCircleOutline } from 'react-icons/md';
import palette from '../../../lib/styles/palette';
// import ActionButtons from '../ActionButtons';
// import AskCancelModal from '../AskCancelModal';

import { standardWeight_Takeup } from '../../../lib/data/standard-weight';
import { toFixedSafe } from '../../../lib/styles/perttierNumber';

const TakeupForm = ({
    onSaveStructure,
    onChangeWeightSummary,
    takeups,
    horizontalLength,
    beltWidth,
    carcassType,
    drives,
}) => {
    const [formData, setFormData] = useState({
        newTakeup: takeups || [],
        id: '',
        takeupTypeCategory: '',
        // tension: tension,    가져오기전에 먼저 입력해 보자.
        tension: '',
        stroke: '',
        factor: 1.0,
        weightBox: '',
        weightGuidePipe: '',
        weightTower: '',
        weightTruck: '',
        weightWire: '',
        weight: '',
    });
    const [isShow, setIsShow] = useState(takeups?.length || false);
    // const [modal, setModal] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const [totalQty, setTotalQty] = useState(0);
    const [totalLength, setTotalLength] = useState(0);
    const [totalWeight, setTotalWeight] = useState(0);

    /* T2 Tension 가져오기 */
    useEffect(() => {
        if (!drives || !Object.keys(drives)) {
            return;
        } else {
            const minValue = drives.reduce((minValue, currentObject) => {
                return currentObject.slackSideTension?.load < minValue
                    ? currentObject.slackSideTension?.load
                    : minValue;
            }, drives[0]?.slackSideTension?.load);

            const tensionForTakeup = minValue / 9.806 / 1000; // N -> ton 단위환산

            setFormData((prev) => ({
                ...prev,
                tension: Math.round(tensionForTakeup * 10) / 10,
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 처음 마운트할 때만 실행

    /* Stroke 계산하기 */
    useEffect(() => {
        if (!beltWidth || !carcassType || !horizontalLength) {
            return;
        }

        let stroke;
        if (carcassType === 'ST') {
            stroke = 0.003 * horizontalLength + 0.5 + beltWidth / 1000;
        } else {
            stroke = 0.03 * horizontalLength + 0.5 + beltWidth / 1000;
        }

        setFormData((prev) => ({
            ...prev,
            stroke: Math.ceil(stroke * 10) / 10, // 소숫점 1자리
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 처음 마운트할 때만 실행

    // const initialTakeup = useRef(takeups || []);
    /* category 설명하는 text */
    const takeupTypeCategoryText = useRef({
        0: 'Vertical Gravity',
        1: 'Horizontal Gravity',
        2: 'Tail Screw',
    });

    const weightLibrary = useRef(
        standardWeight_Takeup.find((item) => item.beltWidth === Number(beltWidth)) || undefined,
    );

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const handleCategoryChange = useCallback((e) => {
        const { name, value } = e.target;
        // console.log('category value : ', value);
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // console.log('formData.takeupTypeCategory : ', formData.takeupTypeCategory);
    }, []);

    /* 마지막 ID 가져오기 */
    const getLastId = useCallback(
        (e) => {
            if (!formData.newTakeup.length) {
                return 0;
            }
            return Math.max(...formData.newTakeup.map((item) => Number(item.id.substring(3))), 0);
        },
        [formData.newTakeup],
    );

    /* 전체 수량 계산 함수 */
    const getTotalQty = useCallback(() => {
        const totalQty = formData.newTakeup.reduce((accumulator, currentValue) => {
            return accumulator + parseInt(currentValue.qty);
        }, 0);
        setTotalQty(totalQty);
    }, [formData.newTakeup]);

    /* 전체 길이 계산 함수 */
    const getTotalLength = useCallback(() => {
        const totalLength = formData.newTakeup.reduce((accumulator, currentValue) => {
            return accumulator + parseInt(currentValue.length);
        }, 0);
        setTotalLength(totalLength);
    }, [formData.newTakeup]);

    /* 전체 중량 계산 함수 */
    const getTotalWeight = useCallback(() => {
        const totalWeight = formData.newTakeup.reduce((accumulator, currentValue) => {
            return accumulator + currentValue.weight;
        }, 0);
        setTotalWeight(totalWeight);
    }, [formData.newTakeup]);

    /* 입력 폼 초기화 함수 */
    const initializeForm = useCallback(() => {
        setFormData((prev) => ({
            ...prev,
            id: `TU-${getLastId() + 1}`,
            // tension: '',     // 계산하여 입력한 값이 초기화되는 문제가 있다.
            // stroke: '',
            weightBox: '',
            weightGuidePipe: '',
            weightTower: '',
            weightTruck: '',
            weightWire: '',
            weight: '',
        }));
    }, [getLastId]);

    /* Tension이 입력되면 index number 생성하기 */
    useEffect(() => {
        // console.log('formData.tension : ', formData.tension);
        // console.log('formData.takeupTypeCategory : ', formData.takeupTypeCategory);
        // console.log('weightLibrary : ', weightLibrary.current);
        if (!formData.takeupTypeCategory || !formData.tension || !weightLibrary.current) return;

        let weightBox = '';
        let weightGuidePipe = '';
        let weightTower = '';
        let weightTruck = '';
        let weightRail = '';
        let weightWire = '';
        let weight = '';

        if (formData.takeupTypeCategory === '0') {
            // Vertical Gravity이면
            const T = formData.tension;
            const guidePipeHeight = formData.stroke + 1;
            const index =
                T <= 1.4
                    ? 0
                    : T <= 2.3
                    ? 1
                    : T <= 3.6
                    ? 2
                    : T <= 4.5
                    ? 3
                    : T <= 5.7
                    ? 4
                    : T <= 7.2
                    ? 5
                    : T <= 9.9
                    ? 6
                    : 7;
            weightBox = weightLibrary.current.weightBoxVertical[index];
            weightGuidePipe = Math.ceil(42.8 * guidePipeHeight);
            weightTower = '-';
            weightTruck = '-';
            weightRail = '-';
            weightWire = '-';
            weight = Number(weightBox) + Number(weightGuidePipe);
        } else if (formData.takeupTypeCategory === '1') {
            // Horizontal Gravity이면
            const T = formData.tension;
            const H = formData.stroke + 3; // stroke를 2로 나누고 필요한 값을 더하는 것이 옳지 않나?
            const tensionIndex =
                T <= 1.4
                    ? 0
                    : T <= 2.3
                    ? 1
                    : T <= 3.6
                    ? 2
                    : T <= 4.5
                    ? 3
                    : T <= 5.7
                    ? 4
                    : T <= 7.2
                    ? 5
                    : T <= 9.9
                    ? 6
                    : 7;
            const ropeTensionIndex =
                T <= 2.8
                    ? 0
                    : T <= 4.6
                    ? 1
                    : T <= 7.2
                    ? 2
                    : T <= 9.0
                    ? 3
                    : T <= 11.4
                    ? 4
                    : T <= 14.4
                    ? 5
                    : T <= 20.0
                    ? 6
                    : 7;
            const heightIndex = H <= 8 ? 0 : H <= 12 ? 1 : H <= 15 ? 2 : 3;
            weightBox = weightLibrary.current.weightBoxHorizontal[tensionIndex];
            weightGuidePipe = '-';
            weightTower = weightLibrary.current.tower[heightIndex][tensionIndex];
            weightTruck = weightLibrary.current.truck[tensionIndex];
            weightRail = 150 * H;
            weightWire =
                weightLibrary.current.wire[ropeTensionIndex] * 20 +
                weightLibrary.current.sheaveBracket[ropeTensionIndex] * 1; // 20m를 기준으로 합시다. SheaveBracket 포함
            weight =
                Number(weightBox) + Number(weightTower) + Number(weightTruck) + Number(weightRail) + Number(weightWire);
        } else if (formData.takeupTypeCategory === '2') {
            // Tail Screw 면
            weight = 35;
        }
        setFormData((prev) => ({
            ...prev,
            weightBox,
            weightGuidePipe,
            weightTower,
            weightTruck,
            weightRail,
            weightWire,
            weight: Math.ceil(weight * formData.factor),
        }));
        // console.log('formData : ', formData);
    }, [formData.takeupTypeCategory, formData.factor, formData.stroke, formData.tension]);

    /* 추가 버튼을 누르면 newTakeup에 takeup 추가 */
    const handleAddItem = useCallback(() => {
        if (!formData.weight) {
            alert('Check input(s) before saving!!!');
            return;
        }
        const newItem = {
            id: formData.id,
            standardWeight: formData.standardWeight,
            // 여기부터
            takeupTypeCategory: takeupTypeCategoryText.current[`${formData.takeupTypeCategory}`],
            tension: formData.tension,
            stroke: formData.stroke,
            weightBox: formData.weightBox,
            weightGuidePipe: formData.weightGuidePipe,
            weightTower: formData.weightTower,
            weightTruck: formData.weightTruck,
            weightRail: formData.weightRail,
            weightWire: formData.weightWire,
            // 여기까지는 각 항목별로 다름
            factor: Math.round(formData.factor * 10) / 10, // 소숫점 첫자리까지 보여주기
            weight: formData.weight,
        };
        setFormData((prev) => ({
            ...prev,
            newTakeup: [...prev.newTakeup, newItem],
        }));
        setIsChanged(true);
        // 입력 후 폼 초기화
        // initializeForm();
    }, [
        formData.factor,
        formData.stroke,
        formData.id,
        formData.standardWeight,
        formData.takeupTypeCategory,
        formData.tension,
        formData.weight,
        formData.weightBox,
        formData.weightGuidePipe,
        formData.weightTower,
        formData.weightTruck,
        formData.weightRail,
        formData.weightWire,
    ]);

    const handleDeleteItem = useCallback((id) => {
        setFormData((prev) => ({
            ...prev,
            newTakeup: prev.newTakeup.filter((item) => item.id !== id),
        }));
        setIsChanged(true);
    }, []);

    /* newTakeup에 변화가 생기면 리덕스에 변경사항 쓰기 */
    useEffect(() => {
        getTotalQty();
        getTotalLength();
        getTotalWeight();
        // 입력폼 초기화
        initializeForm();
        onSaveStructure({
            key: 'takeup',
            value: formData.newTakeup,
        });
        onChangeWeightSummary({
            key: 'takeup',
            value: {
                totalQty,
                totalLength,
                totalWeight,
            },
        });
    }, [
        formData.newTakeup,
        getTotalLength,
        getTotalQty,
        getTotalWeight,
        initializeForm,
        onChangeWeightSummary,
        onSaveStructure,
        totalLength,
        totalQty,
        totalWeight,
    ]);

    /* Save 버튼을 누르면 리덕스에 변경사항 쓰기 */
    // const onSaveTakeup = useCallback(() => {
    //     onSaveStructure({
    //         key: 'takeup',
    //         value: formData.newTakeup,
    //     });
    //     onChangeWeightSummary({
    //         key: 'takeup',
    //         value: {
    //             totalQty,
    //             totalLength,
    //             totalWeight,
    //         },
    //     });
    // }, [formData.newTakeup, onChangeWeightSummary, onSaveStructure, totalLength, totalQty, totalWeight]);

    /* Cancel Change 버튼을 눌렀을 때 */
    // const onClickCancel = useCallback(() => {
    //     // 저장된 적이 없다면 alert
    //     if (!isChanged) {
    //         alert('변경내용이 없습니다.');
    //         return;
    //     }
    //     setModal(true);
    // }, [isChanged]);

    /* 변경사항 취소 */
    // const onCancelChange = useCallback(
    //     (e) => {
    //         // state의 현재 객체를 초기값으로 변경
    //         setFormData((prev) => ({
    //             ...prev,
    //             newTakeup: initialTakeup.current,
    //         }));
    //         setIsChanged(false);
    //         // 리덕스에 현재값(초기값) 쓰기
    //         onSaveStructure({
    //             key: 'takeup',
    //             value: formData.newTakeup,
    //         });
    //         onChangeWeightSummary({
    //             key: 'takeup',
    //             value: {},
    //         });
    //         // 모달창 닫기
    //         setModal(false);
    //     },
    //     [formData.newTakeup, onChangeWeightSummary, onSaveStructure],
    // );

    return (
        <TakeupFormBlock>
            <Title>
                <h3 style={{ width: '400px', margin: '0' }}>Take-up</h3>
                <div style={{ width: '120px' }}>Q'ty: {totalQty} ea</div>
                <div style={{ width: '170px' }}>Weight: {totalWeight} kg</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', display: 'flex', alignItems: 'flex-end' }}>
                    {!isShow ? (
                        <MdExpandMore color="orange" onClick={() => setIsShow(true)} />
                    ) : (
                        <MdExpandLess color="orange" onClick={() => setIsShow(false)} />
                    )}
                </div>
            </Title>
            {isShow && (
                <>
                    <table>
                        <thead>
                            <tr>
                                <th>ID.</th>
                                <th>Takeup Type</th>
                                <th className="modal" onClick={() => alert('Pulley 보기')}>
                                    Tension
                                    <br />
                                    (ton)
                                </th>
                                <th>
                                    stroke
                                    <br />
                                    (m)
                                </th>
                                <th>
                                    Weight Box
                                    <br />
                                    (kg)
                                </th>
                                <th>
                                    Guide Pipe
                                    <br />
                                    (kg)
                                </th>
                                <th>
                                    Tower
                                    <br />
                                    (kg)
                                </th>
                                <th>
                                    Truck
                                    <br />
                                    (kg)
                                </th>
                                <th>
                                    Rail
                                    <br />
                                    (kg)
                                </th>
                                <th>
                                    Wire Etc.
                                    <br />
                                    (kg)
                                </th>
                                <th>Factor</th>
                                {/* <th>Qty</th> */}
                                {/* <th>Unit W't<br/>(kg/set)</th> */}
                                <th>중량(kg)</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {formData.newTakeup &&
                                formData.newTakeup.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.id}</td>
                                        <td>{item.takeupTypeCategory}</td>
                                        <td>{item.tension}</td>
                                        <td>{item.stroke}</td>
                                        <td>{item.weightBox}</td>
                                        <td>{item.weightGuidePipe}</td>
                                        <td>{item.weightTower}</td>
                                        <td>{item.weightTruck}</td>
                                        <td>{item.weightRail}</td>
                                        <td>{item.weightWire}</td>
                                        <td>{toFixedSafe(item.factor, 1)}</td>
                                        <td className="right-align">{item.weight}</td>
                                        <td className="btn-delete" onClick={() => handleDeleteItem(item.id)}>
                                            <MdRemoveCircleOutline />
                                        </td>
                                    </tr>
                                ))}
                            <tr>
                                <td></td>
                            </tr>{' '}
                            {/* 행 사이의 간격을 띄우기 위해 */}
                            <tr>
                                <td></td>
                            </tr>{' '}
                            {/* 행 사이의 간격을 띄우기 위해 */}
                            <tr className="input-form">
                                {/* <td><StyledInput readOnly value={id} onChange={(e) => setId(e.target.value)} /></td> */}
                                <td className="id-column">{formData.id}</td>
                                <td>
                                    <div className="input-radio">
                                        <label>
                                            <input
                                                type="radio"
                                                name="takeupTypeCategory"
                                                value="0"
                                                onChange={handleCategoryChange}
                                            />
                                            <span>Vertical Gravity</span>
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name="takeupTypeCategory"
                                                value="1"
                                                onChange={handleCategoryChange}
                                            />
                                            <span>Horizontal Gravity</span>
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name="takeupTypeCategory"
                                                value="2"
                                                onChange={handleCategoryChange}
                                            />
                                            <span>Tail Screw</span>
                                        </label>
                                    </div>
                                </td>
                                <td>
                                    <StyledInput
                                        placeholder="..."
                                        name="tension"
                                        value={formData.tension}
                                        onChange={handleChange}
                                    />
                                </td>
                                <td>
                                    <StyledInput
                                        placeholder="..."
                                        name="stroke"
                                        value={formData.stroke}
                                        onChange={handleChange}
                                    />
                                </td>
                                <td>{formData.weightBox}</td>
                                <td>{formData.weightGuidePipe}</td>
                                <td>{formData.weightTower}</td>
                                <td>{formData.weightTruck}</td>
                                <td>{formData.weightRail}</td>
                                <td>{formData.weightWire}</td>
                                <td>
                                    <StyledInput
                                        placeholder="..."
                                        name="factor"
                                        value={formData.factor}
                                        onChange={handleChange}
                                    />
                                </td>
                                <td className="right-align total-weight">{formData.weight}</td>
                                <td className="btn-add" onClick={handleAddItem}>
                                    <MdAdd />
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={11}>Take-up Total</td>
                                <td className="right-align">{totalWeight}</td>
                            </tr>
                        </tfoot>
                    </table>
                    {/* <ActionButtons
                    confirmText="Save"
                    cancelText="Cancel Change"
                    onConfirm={onSaveTakeup}
                    onCancel={onClickCancel}
                />
                <AskCancelModal visible={modal} onConfirm={onCancelChange} onCancel={() => setModal(false)} /> */}
                </>
            )}
        </TakeupFormBlock>
    );
};

export default TakeupForm;

/* =================   Styles   ================== */
const Title = styled.div`
    display: flex;
    align-items: flex-end;
    margin-bottom: 1rem;

    color: ${palette.cyan[6]};
`;

const TakeupFormBlock = styled.div`
    // max-width: 1280px;
    // margin: 0 auto;
    // margin: 0 2rem;
    .btn-delete {
        color: ${palette.cyan[6]};
        font-size: 1rem;
        cursor: pointer;
        padding: 0 1rem;
        padding-top: 0.3rem;
        &:hover {
            color: ${palette.cyan[4]};
        }
    }
    .btn-add {
        color: ${palette.cyan[6]};
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0;
        padding-top: 0.4rem;
        &:hover {
            color: ${palette.cyan[4]};
        }
    }
    table {
        width: 100%;
        font-size: 0.9rem;
        border-collapse: collapse;
    }
    thead {
        border-top: 1px solid ${palette.gray[4]};
        border-bottom: 1px solid ${palette.gray[4]};
        color: ${palette.gray[7]};
    }
    tfoot {
        border-top: 1px solid ${palette.gray[4]};
        border-bottom: 1px solid ${palette.gray[4]};
        height: 2.5rem;
        font-weight: bold;
        color: ${palette.gray[7]};
    }
    th {
        padding: 0.5rem 0.25rem;
        text-align: center;
    }
    td {
        padding: 0.25rem 0.25rem;
        text-align: center;
    }
    .td-radio {
        display: flex;
        justify-content: center;
        // margin: 0 auto;
    }
    .input-radio {
        display: flex;
        flex-direction: column;
        // margin: 0 auto;
        align-items: flex-start;
        span {
            margin-left: 0.5rem;
        }
        // 추가함
        label + label {
            margin-top: 0.25rem;
        }
    }
    h3 {
        color: ${palette.cyan[6]};
    }
    .input-form {
        background-color: ${palette.gray[0]};
    }
    .right-align {
        text-align: right;
    }
    .modal {
        color: ${palette.cyan[8]};
        &:hover {
            cursor: pointer;
            color: ${palette.cyan[6]};
        }
    }
`;

const StyledInput = styled.input`
    border: none;
    outline: none;
    padding: 0.25rem 0.5rem;
    width: 50px;
    text-align: center;
    background: transparent;
    &:hover {
        background: ${palette.gray[3]};
    }
`;
